import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Pencil, Trash2, Plus, X, EyeOff, ImageIcon } from "lucide-react";
import { useSeo } from "../useSeo";
import { SPECIAL_EVENTS } from "../data";
import { API_BASE, apiFetch, apiPost, apiPatch, apiDelete } from "../../api/client";
import { getEffectiveRoles } from "../../roleSimulator";

// Ultima agenda carregada da API nesta sessao da SPA (modulo so e avaliado
// uma vez) -- cache simples em memoria, separado por publico/gerenciavel
// (quem pode gerenciar ve itens ocultos que o publico nao ve). Evita o
// "pisca o fallback estatico de 2 eventos e troca pelos demais" que
// acontecia toda vez que a pessoa navegava pra /eventos de novo, mesmo
// ja tendo carregado a agenda real minutos antes.
let cachedPublicEvents = null;
let cachedManageEvents = null;

function nextSunday(now, cutoffHour, cutoffMinute) {
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diff = (7 - d.getDay()) % 7;
  d.setDate(d.getDate() + diff);
  if (diff === 0) {
    const cutoff = new Date(now.getFullYear(), now.getMonth(), now.getDate(), cutoffHour, cutoffMinute);
    if (now >= cutoff) d.setDate(d.getDate() + 7);
  }
  return d;
}
// Usa getters UTC, nao locais -- "date" vem do banco como so-data (meia-noite
// UTC), e o fuso do Brasil (UTC-3) faz Date.getDate()/getMonth() (locais)
// devolverem o dia ANTERIOR (ex.: 2026-09-26 vira "25/09" na tela). As datas
// calculadas aqui mesmo no front (cafe da manha, ver nextSunday) sao criadas
// com o construtor local (meia-noite local), que em UTC-3 cai as 03h do
// mesmo dia -- entao os getters UTC continuam corretos pra elas tambem.
function fmtDDMM(d) {
  return String(d.getUTCDate()).padStart(2, "0") + "/" + String(d.getUTCMonth() + 1).padStart(2, "0");
}

// Schema.org Event (JSON-LD) -- mesma ideia do FAQPage em Faq.jsx: injeta via
// useEffect, so cobre o que rastreadores que executam JS veem (limitacao de
// SPA ja documentada em useSeo.js), mas ajuda em buscas tipo "eventos em
// Osasco" e no Google Eventos. So usa o que ja esta cadastrado -- nunca
// inventa horario ou local que a agenda nao informa.
function parseTimeFromWhen(when) {
  if (!when) return null;
  const match = when.match(/(\d{1,2})h(\d{2})?/);
  if (!match) return null;
  const hour = Number(match[1]);
  const minute = match[2] ? Number(match[2]) : 0;
  if (hour > 23 || minute > 59) return null;
  return { hour, minute };
}

function isoDateUTC(d) {
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
}

const EVENT_LOCATION = {
  "@type": "Place",
  name: "Igreja Batista Mosaico",
  address: {
    "@type": "PostalAddress",
    streetAddress: "Av. dos Autonomistas, 2233",
    addressLocality: "Osasco",
    addressRegion: "SP",
    addressCountry: "BR"
  }
};

const SUNDAY_SERVICE_TITLE = "Café da manhã & Culto de Celebração";

function buildEventsSchema(events) {
  if (!events || events.length === 0) return null;
  const items = events.map((e) => {
    const firstWhen = (e.when || "").split(" · a cada ")[0];
    const time = parseTimeFromWhen(firstWhen);
    const dateStr = isoDateUTC(e.date);
    const startDate = time
      ? `${dateStr}T${String(time.hour).padStart(2, "0")}:${String(time.minute).padStart(2, "0")}:00-03:00`
      : dateStr;
    const entry = {
      "@type": "Event",
      name: e.title,
      startDate,
      eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
      eventStatus: "https://schema.org/EventScheduled",
      location: EVENT_LOCATION,
      organizer: { "@type": "Organization", name: "Igreja Batista Mosaico", url: "https://www.batistamosaico.com.br/" }
    };
    // So o culto de domingo tem hora de termino conhecida (mesma da
    // openingHoursSpecification do Church, em public/index.html).
    if (e.title === SUNDAY_SERVICE_TITLE && time) {
      entry.endDate = `${dateStr}T11:30:00-03:00`;
    }
    return entry;
  });
  return { "@context": "https://schema.org", "@graph": items };
}

function useEventsSchema(events) {
  useEffect(() => {
    const schema = buildEventsSchema(events);
    if (!schema) return undefined;
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "events-schema";
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, [events]);
}

// Opcoes fixas de recorrencia funcional (ver recurrenceIntervalDays no
// backend) -- "custom" libera o campo de dias ao lado. recurrenceLabel
// (texto livre, so cosmetico) continua existindo separado, pro selo "a
// cada X" poder ter um texto diferente do numero puro de dias.
const RECURRENCE_PRESETS = [
  { value: "", label: "Não se repete" },
  { value: "7", label: "Semanal (7 dias)" },
  { value: "14", label: "Quinzenal (14 dias)" },
  { value: "30", label: "Mensal (30 dias)" },
  { value: "custom", label: "Personalizado" }
];

// Mesma logica de fmtDDMM: usa getters UTC porque a data vem do banco
// como so-data (meia-noite UTC) -- getters locais empurrariam o dia pra
// tras no fuso do Brasil.
function utcDateKey(d) {
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
}

function addUTCDays(d, days) {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + days));
}

// Gera um lote generoso de proximas ocorrencias de um evento recorrente,
// a partir da data ancora, pulando qualquer data que esteja em
// recurrenceSkipDates. O lote e generoso (30 passos) pra sempre sobrar
// ocorrencia futura mesmo se a ancora ja tiver ficado velha ou o
// intervalo for grande (ex.: a cada 60 dias) -- o filtro de "so datas
// futuras" acontece depois, no useMemo de events.
const RECURRENCE_MAX_ITERATIONS = 30;

function generateRecurringOccurrences(event) {
  const interval = event.recurrenceIntervalDays;
  if (!interval || interval < 1) return [];
  const skipKeys = new Set((event.recurrenceSkipDates || []).map((d) => utcDateKey(d)));
  const occurrences = [];
  let cursor = event.date;
  for (let i = 0; i < RECURRENCE_MAX_ITERATIONS; i += 1) {
    const key = utcDateKey(cursor);
    if (!skipKeys.has(key)) {
      occurrences.push({
        id: event.id,
        raw: event.raw,
        title: event.title,
        date: cursor,
        when: event.when,
        where: event.where,
        kind: event.kind,
        isActive: event.isActive,
        isRecurringOccurrence: true,
        occurrenceDateKey: key
      });
    }
    cursor = addUTCDays(cursor, interval);
  }
  return occurrences;
}

// Reconstroi, a partir de recurrenceIntervalDays gravado, qual opcao do
// select ficaria selecionada ao editar (7/14/30 caem num preset fixo,
// qualquer outro numero vira "custom" com o campo de dias preenchido).
function presetFromIntervalDays(days) {
  if (!days) return { recurrencePreset: "", recurrenceCustomDays: "" };
  if ([7, 14, 30].includes(days)) return { recurrencePreset: String(days), recurrenceCustomDays: "" };
  return { recurrencePreset: "custom", recurrenceCustomDays: String(days) };
}

const KIND_OPTIONS = ["evento", "culto"];
const KIND_LABELS = { evento: "Evento", culto: "Culto" };
const EMPTY_FORM = {
  title: "",
  date: "",
  timeLabel: "",
  location: "",
  kind: "evento",
  recurrenceLabel: "",
  recurrencePreset: "",
  recurrenceCustomDays: "",
  isActive: true,
  bannerUrl: ""
};

function dateForInput(value) {
  if (!value) return "";
  const iso = String(value);
  return iso.length >= 10 ? iso.slice(0, 10) : "";
}

function formToBody(form) {
  const { recurrencePreset, recurrenceCustomDays, ...rest } = form;
  let recurrenceIntervalDays = null;
  if (recurrencePreset === "custom") {
    const n = Number(recurrenceCustomDays);
    recurrenceIntervalDays = Number.isFinite(n) && n > 0 ? Math.round(n) : null;
  } else if (recurrencePreset) {
    recurrenceIntervalDays = Number(recurrencePreset);
  }
  return { ...rest, recurrenceIntervalDays };
}

// Reconstroi o campo "when" (ex.: "Qui · 20h · a cada 15 dias") a partir dos
// campos separados que a API devolve -- mantem o mesmo formato que o resto
// desta pagina (e o filtro de recorrencia logo abaixo) ja esperava quando
// os eventos vinham fixos em SPECIAL_EVENTS. Carrega tambem id/isActive/raw
// pra quem tem permissao de edicao poder editar/excluir direto aqui.
function fromApiEvent(event) {
  return {
    id: event.id,
    isActive: event.isActive !== false,
    raw: event,
    title: event.title,
    date: new Date(event.date),
    when: event.recurrenceLabel ? `${event.timeLabel} · a cada ${event.recurrenceLabel}` : event.timeLabel,
    where: event.location,
    kind: event.kind,
    recurrenceIntervalDays: event.recurrenceIntervalDays || null,
    recurrenceSkipDates: (event.recurrenceSkipDates || []).map((d) => new Date(d)),
    bannerUrl: event.bannerUrl || null
  };
}

// Estilos dos controles de edição inline (só aparecem pra quem tem permissão
// -- ADMIN ou EDITOR_CONTEUDO). Paleta consistente com o antigo painel
// /admin/eventos, que esta tela substitui.
const iconButtonStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 30,
  height: 30,
  borderRadius: 8,
  border: "1px solid rgba(148,163,184,0.28)",
  background: "rgba(255,255,255,0.03)",
  color: "#c7ced9",
  cursor: "pointer"
};

const createButtonStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  minHeight: 36,
  padding: "0 14px",
  border: "1px solid rgba(74,222,128,0.4)",
  background: "rgba(34,197,94,0.16)",
  color: "#86EFAC",
  fontSize: 12,
  fontWeight: 900,
  borderRadius: 8,
  cursor: "pointer",
  flex: "none"
};

const modalBackdropStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(4,8,18,0.72)",
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "center",
  padding: 20,
  overflowY: "auto",
  zIndex: 50
};

const modalCardStyle = {
  width: "100%",
  maxWidth: 560,
  margin: "3vh auto",
  background: "linear-gradient(160deg, #0f1727, #131f34)",
  border: "1px solid rgba(120,152,210,0.32)",
  borderRadius: 16,
  padding: 22,
  boxShadow: "0 24px 60px rgba(0,0,0,0.5)",
  position: "relative"
};

const modalCloseButtonStyle = {
  position: "absolute",
  top: 14,
  right: 14,
  width: 28,
  height: 28,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  border: "none",
  background: "rgba(255,255,255,0.06)",
  borderRadius: 8,
  color: "#9fb0cc",
  cursor: "pointer"
};

const inputStyle = {
  width: "100%",
  boxSizing: "border-box",
  padding: "10px 12px",
  borderRadius: 8,
  border: "1px solid rgba(148,163,184,0.32)",
  background: "rgba(255,255,255,0.04)",
  color: "#e5e7eb",
  fontSize: 14,
  fontFamily: "inherit",
  colorScheme: "dark"
};

const selectStyle = { ...inputStyle };

const fieldLabelStyle = {
  display: "block",
  margin: "0 0 6px",
  fontSize: 12,
  fontWeight: 800,
  color: "#9fb0cc"
};

const primaryButtonStyle = {
  minHeight: 40,
  padding: "0 18px",
  borderRadius: 9,
  border: "1px solid rgba(125,211,252,0.4)",
  background: "rgba(14,165,233,0.18)",
  color: "#BAE6FD",
  fontSize: 13,
  fontWeight: 900,
  cursor: "pointer"
};

const secondaryButtonStyle = {
  minHeight: 40,
  padding: "0 18px",
  borderRadius: 9,
  border: "1px solid rgba(148,163,184,0.32)",
  background: "transparent",
  color: "#c7ced9",
  fontSize: 13,
  fontWeight: 800,
  cursor: "pointer"
};

const dangerButtonStyle = {
  minHeight: 40,
  padding: "0 18px",
  borderRadius: 9,
  border: "1px solid rgba(248,113,113,0.4)",
  background: "rgba(248,113,113,0.14)",
  color: "#FCA5A5",
  fontSize: 13,
  fontWeight: 900,
  cursor: "pointer"
};

function FieldRow({ children, columns = 2 }) {
  return <div style={{ display: "grid", gridTemplateColumns: `repeat(${columns}, 1fr)`, gap: 12, marginBottom: 12 }}>{children}</div>;
}

function Field({ label, required, ...rest }) {
  return (
    <div>
      <label style={fieldLabelStyle}>
        {label}
        {required && <span style={{ color: "#f0a84e", fontWeight: 700 }}> *</span>}
      </label>
      <input style={inputStyle} {...rest} />
    </div>
  );
}

export default function Eventos() {
  useSeo({
    title: "Agenda de Eventos | Igreja Batista Mosaico",
    description: "Confira a agenda pública de eventos da Igreja Batista Mosaico em Osasco: cultos, encontros e programação da semana.",
    path: "/eventos"
  });

  // viewerUser vem do InstitutionalLayout (mesma checagem de sessão que já
  // decide o "Bem-vindo" no topo) -- reaproveitada aqui pra decidir se esta
  // pessoa pode editar a agenda direto nesta tela, sem precisar de um painel
  // de admin separado (pedido: "poderia ser direto na tela para quem ter
  // permissao"). Editar deixou de existir em /admin/eventos -- essa tela é
  // agora o único lugar que gerencia a agenda pública.
  const { viewerUser } = useOutletContext() || {};
  const effectiveRoles = getEffectiveRoles(viewerUser);
  const canManage = Boolean(
    viewerUser &&
    !viewerUser.passwordMustChange &&
    (effectiveRoles.includes("ADMIN") || effectiveRoles.includes("EDITOR_CONTEUDO"))
  );

  // Começa com o conteúdo estático como fallback (garante que a página nunca
  // fica vazia) e troca pelo cadastro assim que a API responder -- quem não
  // pode gerenciar vê só o que está ativo (/events/public); quem pode
  // gerenciar vê tudo, incluindo o que está oculto, pra poder reativar.
  const [specialEvents, setSpecialEvents] = useState(
    () => (canManage ? cachedManageEvents : cachedPublicEvents) || SPECIAL_EVENTS
  );
  const [manageLoading, setManageLoading] = useState(false);
  const [manageFeedback, setManageFeedback] = useState("");

  const [formOpen, setFormOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formSaving, setFormSaving] = useState(false);
  const [formError, setFormError] = useState("");

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  // Lightbox do banner -- ver bannerUrl no card abaixo (pedido do Drigo,
  // 17-19/10/2026: mostrar o banner cadastrado do evento ao clicar no card).
  const [lightboxImage, setLightboxImage] = useState(null);

  const loadManageEvents = useCallback(async () => {
    setManageLoading(true);
    setManageFeedback("");
    try {
      const data = await apiFetch("/events", { method: "GET" });
      if (Array.isArray(data)) {
        const mapped = data.map(fromApiEvent);
        cachedManageEvents = mapped;
        setSpecialEvents(mapped);
      }
    } catch (error) {
      setManageFeedback("Não foi possível carregar a agenda completa agora.");
    } finally {
      setManageLoading(false);
    }
  }, []);

  useEffect(() => {
    let alive = true;
    if (canManage) {
      loadManageEvents();
      return () => { alive = false; };
    }
    fetch(`${API_BASE}/events/public`)
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error("falha ao buscar eventos"))))
      .then((data) => {
        if (alive && Array.isArray(data)) {
          const mapped = data.map(fromApiEvent);
          cachedPublicEvents = mapped;
          setSpecialEvents(mapped);
        }
      })
      .catch(() => {
        // Mantém o fallback estático em caso de erro -- agenda pública nunca
        // fica em branco por causa de uma falha de rede.
      });
    return () => {
      alive = false;
    };
  }, [canManage, loadManageEvents]);

  // Cada evento recorrente (recurrenceIntervalDays preenchido) contribui, por
  // padrao, so a proxima ocorrencia -- exatamente como um evento comum. So
  // quando a lista combinada fica com menos de 4 itens (MIN_EVENTS_ON_AGENDA)
  // e que puxamos ocorrencias futuras extras das mesmas series, em rodizio,
  // pra nunca deixar a agenda parecendo vazia. O especial de domingo (Cafe
  // da manha & Culto) continua com sua propria logica fixa de sempre.
  const events = useMemo(() => {
    const today = new Date();
    // Datas vindas do banco sao date-only (meia-noite UTC, ver comentario
    // no topo do arquivo) -- por isso o corte de "hoje" tambem precisa ser
    // ancorado em UTC, e nao em meia-noite local. Com Brasil em UTC-3, um
    // todayFloor em horario local (ex.: 26/09 00h local = 26/09 03h UTC)
    // ficava DEPOIS da meia-noite UTC do proprio dia do evento, entao um
    // evento de hoje (26/09 00h UTC) era filtrado como "passado" assim que
    // batia meia-noite local -- sumindo da agenda horas antes de acontecer.
    const todayFloor = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()));
    const firstSunday = nextSunday(today, 11, 30);
    const sundayEvents = [0, 1].map((w) => {
      const sd = new Date(firstSunday.getFullYear(), firstSunday.getMonth(), firstSunday.getDate() + w * 7);
      return { title: "Café da manhã & Culto de Celebração", date: sd, when: "Dom · 9h e 10h", where: "Osasco", kind: "culto" };
    });

    const oneOff = specialEvents.filter((e) => !e.recurrenceIntervalDays);
    const recurring = specialEvents.filter((e) => e.recurrenceIntervalDays);

    const queues = recurring.map((event) =>
      generateRecurringOccurrences(event).filter((occ) => occ.date >= todayFloor)
    );

    const picked = queues.map((queue) => queue.shift()).filter(Boolean);

    const MIN_EVENTS_ON_AGENDA = 4;
    let combinedCount = oneOff.filter((e) => e.date >= todayFloor).length + sundayEvents.length + picked.length;
    let stillHasMore = true;
    while (combinedCount < MIN_EVENTS_ON_AGENDA && stillHasMore) {
      stillHasMore = false;
      for (const queue of queues) {
        if (combinedCount >= MIN_EVENTS_ON_AGENDA) break;
        const next = queue.shift();
        if (next) {
          picked.push(next);
          combinedCount += 1;
          stillHasMore = true;
        }
      }
    }

    return oneOff.concat(sundayEvents, picked)
      .filter((e) => e.date >= todayFloor)
      .sort((a, b) => a.date - b.date);
  }, [specialEvents]);

  useEventsSchema(events);

  const openCreateForm = () => {
    setEditingEvent(null);
    setForm(EMPTY_FORM);
    setFormError("");
    setFormOpen(true);
  };

  const openEditForm = (event) => {
    const raw = event.raw || {};
    const { recurrencePreset, recurrenceCustomDays } = presetFromIntervalDays(raw.recurrenceIntervalDays);
    setEditingEvent({
      id: event.id,
      title: event.title,
      isRecurring: Boolean(raw.recurrenceIntervalDays),
      skipDates: raw.recurrenceSkipDates || []
    });
    setForm({
      title: raw.title || "",
      date: dateForInput(raw.date),
      timeLabel: raw.timeLabel || "",
      location: raw.location || "",
      kind: raw.kind || "evento",
      recurrenceLabel: raw.recurrenceLabel || "",
      recurrencePreset,
      recurrenceCustomDays,
      isActive: raw.isActive !== false,
      bannerUrl: raw.bannerUrl || ""
    });
    setFormError("");
    setFormOpen(true);
  };

  const closeForm = () => {
    if (formSaving) return;
    setFormOpen(false);
    setEditingEvent(null);
    setFormError("");
  };

  const updateField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const saveForm = async () => {
    if (!form.title.trim()) {
      setFormError("Título é obrigatório.");
      return;
    }
    if (!form.date) {
      setFormError("Data é obrigatória.");
      return;
    }
    if (!form.timeLabel.trim()) {
      setFormError('Preencha o horário (ex.: "Qui · 20h").');
      return;
    }
    if (form.recurrencePreset === "custom" && !(Number(form.recurrenceCustomDays) > 0)) {
      setFormError("Informe de quantos em quantos dias esse evento se repete.");
      return;
    }
    setFormSaving(true);
    setFormError("");
    try {
      const body = formToBody(form);
      if (editingEvent) {
        await apiPatch(`/events/${encodeURIComponent(editingEvent.id)}`, { body });
      } else {
        await apiPost("/events", { body });
      }
      setFormOpen(false);
      setEditingEvent(null);
      await loadManageEvents();
    } catch (error) {
      setFormError(error?.message || "Não foi possível salvar esse evento.");
    } finally {
      setFormSaving(false);
    }
  };

  const confirmDelete = async () => {
    const event = deleteTarget;
    if (!event) return;
    setDeletingId(event.id);
    setManageFeedback("");
    try {
      if (event.isRecurringOccurrence) {
        await apiPost(`/events/${encodeURIComponent(event.id)}/skip`, { body: { date: event.occurrenceDateKey } });
      } else {
        await apiDelete(`/events/${encodeURIComponent(event.id)}`);
      }
      setDeleteTarget(null);
      await loadManageEvents();
    } catch (error) {
      setManageFeedback(error?.message || "Não foi possível concluir essa ação agora.");
      setDeleteTarget(null);
    } finally {
      setDeletingId(null);
    }
  };

  // Desfaz um "pular esta data" -- volta a ocorrência a aparecer na agenda.
  // Só chamada a partir da lista de datas puladas dentro do form de edição.
  const unskipOccurrence = async (eventId, dateKey) => {
    setManageFeedback("");
    try {
      await apiDelete(`/events/${encodeURIComponent(eventId)}/skip/${encodeURIComponent(dateKey)}`);
      setEditingEvent((prev) =>
        prev && prev.id === eventId
          ? { ...prev, skipDates: prev.skipDates.filter((d) => dateForInput(d) !== dateKey) }
          : prev
      );
      await loadManageEvents();
    } catch (error) {
      setManageFeedback(error?.message || "Não foi possível desfazer essa data pulada agora.");
    }
  };

  return (
    <>
      <div className="section-head" style={{ marginTop: 34, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div>
          <p className="eyebrow">Eventos</p>
          <h1>Agenda pública</h1>
        </div>
        {canManage && (
          <button type="button" onClick={openCreateForm} style={createButtonStyle}>
            <Plus size={14} />
            Novo evento
          </button>
        )}
      </div>

      {canManage && manageFeedback && (
        <p style={{ margin: "0 0 16px", color: "#FCA5A5", fontSize: 13, background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.28)", borderRadius: 10, padding: "10px 12px" }}>
          {manageFeedback}
        </p>
      )}

      <div className="list-stack">
        {events.map((e, i) => {
          const [when, recur] = e.when.split(" · a cada ");
          const isNext = i === 0;
          const hidden = canManage && e.id && e.isActive === false;
          // Clique no card abre o banner ampliado (lightbox) quando o evento
          // tem um cadastrado -- pedido do Drigo (17-19/10/2026). Os botões
          // de editar/excluir ficam "por cima" e cortam a propagação pra não
          // abrir o lightbox junto.
          const hasBanner = Boolean(e.bannerUrl);
          return (
              <div
                key={(e.id || e.title) + i}
                className={"card list-card event-row" + (isNext ? " next-event" : "")}
                style={{
                  ...(hidden ? { opacity: 0.55 } : null),
                  ...(hasBanner ? { cursor: "zoom-in" } : null)
                }}
                onClick={hasBanner ? () => setLightboxImage({ src: e.bannerUrl, alt: e.title }) : undefined}
              >
              <div className="event-date">
                <span className="d">{fmtDDMM(e.date)}</span>
                <span className="t">{when}</span>
              </div>
              <div className="body">
                <h3>
                  {e.title}
                  {isNext && <span className="event-next-badge">Próximo</span>}
                  {hasBanner && <ImageIcon size={13} style={{ marginLeft: 6, verticalAlign: -2, opacity: 0.75 }} />}
                </h3>
                <p className="sub">
                  {e.where}
                  {recur && <span className="event-recur-badge">a cada {recur}</span>}
                  {hidden && <span className="event-recur-badge" style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><EyeOff size={11} /> Oculto</span>}
                </p>
              </div>
              {canManage && e.id && (
                <div
                  style={{ display: "flex", alignItems: "center", gap: 6, flex: "none" }}
                  onClick={(event) => event.stopPropagation()}
                >
                  <button type="button" onClick={() => openEditForm(e)} title="Editar evento" style={iconButtonStyle}>
                    <Pencil size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeleteTarget(e)}
                    disabled={deletingId === e.id || manageLoading}
                    title={e.isRecurringOccurrence ? "Não vai ter dessa vez" : "Excluir evento"}
                    style={iconButtonStyle}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              )}
              </div>
          );
        })}
      </div>

      {lightboxImage && (
        <div
          role="button"
          tabIndex={0}
          aria-label="Fechar imagem ampliada"
          onClick={() => setLightboxImage(null)}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") setLightboxImage(null);
          }}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(5,8,16,0.92)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
            cursor: "zoom-out"
          }}
        >
          <button
            type="button"
            onClick={() => setLightboxImage(null)}
            aria-label="Fechar"
            style={{
              position: "absolute",
              top: 18,
              right: 18,
              width: 36,
              height: 36,
              borderRadius: 999,
              border: "1px solid rgba(255,255,255,0.3)",
              background: "rgba(10,14,24,0.7)",
              color: "#fff",
              fontSize: 18,
              lineHeight: 1,
              cursor: "pointer"
            }}
          >
            ×
          </button>
          <img
            src={lightboxImage.src}
            alt={lightboxImage.alt}
            onClick={(event) => event.stopPropagation()}
            style={{ maxWidth: "min(92vw, 1000px)", maxHeight: "90vh", borderRadius: 16, boxShadow: "0 24px 60px rgba(0,0,0,0.6)", cursor: "default" }}
          />
        </div>
      )}
      <p className="form-note">Café da manhã com Encontro de Celebração acontecem todo domingo, 9h e 10h respectivamente — mostramos aqui só os próximos, para a agenda não ficar repetida.</p>

      {canManage && formOpen && (
        <div style={modalBackdropStyle} onClick={closeForm}>
          <div style={modalCardStyle} onClick={(event) => event.stopPropagation()}>
            <button type="button" onClick={closeForm} style={modalCloseButtonStyle} disabled={formSaving}>
              <X size={16} />
            </button>
            <p style={{ margin: "0 0 4px", fontSize: 11, letterSpacing: 1.2, color: "#7DD3FC", textTransform: "uppercase", fontWeight: 900 }}>
              {editingEvent ? "Editar evento" : "Novo evento"}
            </p>
            <h2 style={{ margin: "0 0 6px", fontSize: 19, fontWeight: 900, color: "#FFF" }}>
              {editingEvent ? editingEvent.title : "Cadastrar na agenda"}
            </h2>

            {formError && (
              <p style={{ margin: "10px 0 0", color: "#FCA5A5", fontSize: 13, background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.28)", borderRadius: 10, padding: "10px 12px" }}>
                {formError}
              </p>
            )}

            <FieldRow columns={1}>
              <Field label="Título" required type="text" value={form.title} onChange={(e) => updateField("title", e.target.value)} />
            </FieldRow>
            <FieldRow>
              <Field label="Data (próxima ocorrência)" required type="date" value={form.date} onChange={(e) => updateField("date", e.target.value)} />
              <Field label="Horário (texto)" required type="text" placeholder="Ex.: Qui · 20h" value={form.timeLabel} onChange={(e) => updateField("timeLabel", e.target.value)} />
            </FieldRow>
            <FieldRow>
              <Field label="Local" type="text" value={form.location} onChange={(e) => updateField("location", e.target.value)} />
              <div>
                <label style={fieldLabelStyle}>Tipo</label>
                <select style={selectStyle} value={form.kind} onChange={(e) => updateField("kind", e.target.value)}>
                  {KIND_OPTIONS.map((option) => (
                    <option key={option} value={option}>{KIND_LABELS[option]}</option>
                  ))}
                </select>
              </div>
            </FieldRow>
            <FieldRow columns={1}>
              <Field
                label="Banner (opcional)"
                type="text"
                placeholder="/institucional/nome-do-arquivo.jpg -- manda a imagem que eu subo e preencho aqui"
                value={form.bannerUrl}
                onChange={(e) => updateField("bannerUrl", e.target.value)}
              />
            </FieldRow>
            <FieldRow>
              <div>
                <label style={fieldLabelStyle}>Repete a cada</label>
                <select style={selectStyle} value={form.recurrencePreset} onChange={(e) => updateField("recurrencePreset", e.target.value)}>
                  {RECURRENCE_PRESETS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
              <Field
                label="Dias (personalizado)"
                type="number"
                min="1"
                disabled={form.recurrencePreset !== "custom"}
                value={form.recurrenceCustomDays}
                onChange={(e) => updateField("recurrenceCustomDays", e.target.value)}
              />
            </FieldRow>
            <FieldRow columns={1}>
              <Field label="Recorrência (opcional, texto do selo)" type="text" placeholder='Ex.: "15 dias" -- vira o selo "a cada 15 dias"' value={form.recurrenceLabel} onChange={(e) => updateField("recurrenceLabel", e.target.value)} />
            </FieldRow>
            {editingEvent && editingEvent.isRecurring && editingEvent.skipDates && editingEvent.skipDates.length > 0 && (
              <FieldRow columns={1}>
                <div>
                  <label style={fieldLabelStyle}>Datas puladas nessa série</label>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {editingEvent.skipDates.map((rawDate) => {
                      const dateKey = dateForInput(rawDate);
                      return (
                        <span
                          key={dateKey}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                            padding: "4px 8px",
                            borderRadius: 999,
                            border: "1px solid rgba(148,163,184,0.32)",
                            background: "rgba(255,255,255,0.04)",
                            color: "#c7ced9",
                            fontSize: 12
                          }}
                        >
                          {dateKey.split("-").reverse().join("/")}
                          <button
                            type="button"
                            onClick={() => unskipOccurrence(editingEvent.id, dateKey)}
                            title="Voltar a mostrar essa data"
                            style={{ border: "none", background: "none", padding: 0, color: "#9fb0cc", cursor: "pointer", display: "inline-flex" }}
                          >
                            <X size={11} />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                </div>
              </FieldRow>
            )}
            <FieldRow columns={1}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#c7ced9", cursor: "pointer" }}>
                <input type="checkbox" checked={form.isActive} onChange={(e) => updateField("isActive", e.target.checked)} />
                Visível na agenda pública
              </label>
            </FieldRow>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginTop: 20 }}>
              {editingEvent && editingEvent.isRecurring ? (
                <button
                  type="button"
                  onClick={() => {
                    setDeleteTarget({ id: editingEvent.id, title: editingEvent.title, isRecurringOccurrence: false });
                    setFormOpen(false);
                  }}
                  disabled={formSaving}
                  style={{ ...dangerButtonStyle, minHeight: 36, padding: "0 14px", fontSize: 12 }}
                >
                  Excluir toda a série
                </button>
              ) : <span />}
              <div style={{ display: "flex", gap: 10 }}>
                <button type="button" onClick={closeForm} disabled={formSaving} style={secondaryButtonStyle}>
                  Cancelar
                </button>
                <button type="button" onClick={saveForm} disabled={formSaving} style={{ ...primaryButtonStyle, opacity: formSaving ? 0.7 : 1 }}>
                  {formSaving ? "Salvando..." : "Salvar"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {canManage && deleteTarget && (
        <div style={modalBackdropStyle} onClick={() => !deletingId && setDeleteTarget(null)}>
          <div style={{ ...modalCardStyle, maxWidth: 420 }} onClick={(event) => event.stopPropagation()}>
            <button type="button" onClick={() => setDeleteTarget(null)} style={modalCloseButtonStyle} disabled={Boolean(deletingId)}>
              <X size={16} />
            </button>
            <p style={{ margin: "0 0 4px", fontSize: 11, letterSpacing: 1.2, color: "#FCA5A5", textTransform: "uppercase", fontWeight: 900 }}>
              {deleteTarget.isRecurringOccurrence ? "Pular esta data" : "Excluir evento"}
            </p>
            <h2 style={{ margin: "0 0 16px", fontSize: 19, fontWeight: 900, color: "#FFF" }}>{deleteTarget.title}</h2>
            <p style={{ margin: "0 0 22px", color: "#9fb0cc", fontSize: 13, lineHeight: 1.6 }}>
              {deleteTarget.isRecurringOccurrence
                ? `Essa ocorrência de ${deleteTarget.occurrenceDateKey.split("-").reverse().join("/")} não vai acontecer? A série continua normalmente nas próximas datas.`
                : 'Remover este evento da agenda? Essa ação não pode ser desfeita. Se for só pra esconder por um tempo, prefira editar e desmarcar "Visível na agenda pública". Se esse evento for recorrente, isso apaga a série inteira.'}
            </p>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button type="button" onClick={() => setDeleteTarget(null)} disabled={Boolean(deletingId)} style={secondaryButtonStyle}>
                Cancelar
              </button>
              <button type="button" onClick={confirmDelete} disabled={Boolean(deletingId)} style={{ ...dangerButtonStyle, opacity: deletingId ? 0.7 : 1 }}>
                {deletingId
                  ? (deleteTarget.isRecurringOccurrence ? "Pulando..." : "Excluindo...")
                  : (deleteTarget.isRecurringOccurrence ? "Pular essa data" : "Excluir definitivamente")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
